import axiosInstance from '../utils/axiosConfig';
import { getAbsoluteMediaUrl, processEmployeeAvatars } from '../utils/mediaUtils';
import keepServerAnswer from '../utils/keepServerAnswer';

/**
 * HMS Colleagues Service
 * Handles API calls for the HMS Colleagues screen
 */
class HMSColleaguesService {
    /**
     * Convert relative media URLs to absolute URLs
     * @param {string} url - Relative URL like "/media/avatars/..."
     * @returns {string} - Absolute URL
     * @deprecated Use getAbsoluteMediaUrl from mediaUtils instead
     */
    getAbsoluteMediaUrl(url) {
        return getAbsoluteMediaUrl(url);
    }

    /**
     * Process employee data to fix avatar URLs
     * @param {Array} employees - Array of employee objects
     * @returns {Array} - Processed employees with absolute avatar URLs
     * @deprecated Use processEmployeeAvatars from mediaUtils instead
     */
    processEmployeeAvatars(employees) {
        return processEmployeeAvatars(employees);
    }

    /**
     * Process birthday data to fix avatar URLs
     * @param {Object} birthdays - Birthday data object
     * @returns {Object} - Processed birthday data with absolute avatar URLs
     */
    processBirthdayAvatars(birthdays) {
        return {
            ...birthdays,
            today: birthdays.today?.map(emp => ({
                ...emp,
                avatar: this.getAbsoluteMediaUrl(emp.avatar)
            })) || [],
            upcoming: birthdays.upcoming?.map(item => ({
                ...item,
                employee: {
                    ...item.employee,
                    avatar: this.getAbsoluteMediaUrl(item.employee.avatar)
                }
            })) || [],
            upcoming_this_month: birthdays.upcoming_this_month?.map(item => ({
                ...item,
                employee: {
                    ...item.employee,
                    avatar: this.getAbsoluteMediaUrl(item.employee.avatar)
                }
            })) || []
        };
    }
    /**
     * Get HMS Colleagues data including employees, birthdays, and stats
     * @returns {Promise<Object>} Colleagues data
     */
    async getColleaguesData() {
        try {
            const response = await axiosInstance.get('/api/hms-colleagues/');
            
            if (response.data.success) {
                // Process the data to fix avatar URLs
                const processedData = {
                    ...response.data,
                    employees: this.processEmployeeAvatars(response.data.employees || []),
                    birthdays: this.processBirthdayAvatars(response.data.birthdays || {})
                };
                
                console.log('🔧 HMS COLLEAGUES SERVICE: Processed avatar URLs');
                return processedData;
            } else {
                throw new Error(response.data.message || 'Failed to load IMT Colleagues data');
            }
        } catch (error) {
            console.error('Error fetching HMS Colleagues data:', error);
            throw keepServerAnswer(error,
                error.response?.data?.message || 
                error.message || 
                'Failed to load IMT Colleagues data'
            );
        }
    }

    /**
     * Generate work style compatibility report
     * @returns {Promise<Object>} Compatibility report data
     */
    async generateCompatibilityReport() {
        try {
            const response = await axiosInstance.post('/api/compatibility-report/');
            
            if (response.data.success) {
                return response.data;
            } else {
                throw new Error(response.data.message || 'Failed to generate compatibility report');
            }
        } catch (error) {
            console.error('Error generating compatibility report:', error);
            throw keepServerAnswer(error,
                error.response?.data?.message || 
                error.message || 
                'Failed to generate compatibility report'
            );
        }
    }

    /**
     * Get HMS Colleagues celebration stats for navbar badge
     * Returns counts of today's birthdays and anniversaries
     * @returns {Promise<Object>} Stats with birthdays_today, anniversaries_today, total_today
     */
    async getColleaguesStats() {
        try {
            const response = await axiosInstance.get('/api/hms-colleagues/stats/');
            
            if (response.data.success) {
                return response.data;
            } else {
                throw new Error(response.data.message || 'Failed to load IMT Colleagues stats');
            }
        } catch (error) {
            console.error('Error fetching HMS Colleagues stats:', error);
            return {
                success: false,
                birthdays_today: 0,
                anniversaries_today: 0,
                total_today: 0
            };
        }
    }
}

const hmsColleaguesService = new HMSColleaguesService();
export default hmsColleaguesService;
